import { toast } from "sonner";
import { Check, X, RotateCcw } from "lucide-react";
import MissionShell from "../MissionShell";
import { Card, Btn } from "@/components/kit";
import { QUIZ } from "../content";
import { useProgress } from "../store";
import { isDone, quizScore, QUIZ_PASS } from "../logic";

function Question({ q, idx, answer, submitted, onPick }) {
  return (
    <fieldset className="space-y-3" data-testid={`quiz-question-${idx + 1}`}>
      <legend className="mb-3 font-medium text-ink"><span className="mr-2 font-display text-gold">{idx + 1}.</span>{q.question}</legend>
      {q.options.map((opt, i) => {
        const picked = answer === i;
        const correct = q.correct === i;
        let cls = picked ? "border-bx bg-bx/[0.04]" : "border-line hover:border-bx/30";
        if (submitted) cls = correct ? "border-[#3F7A5A]/50 bg-[#3F7A5A]/[0.06]" : picked ? "border-[#B5485A]/50 bg-[#B5485A]/[0.05]" : "border-line opacity-70";
        return (
          <button key={i} type="button" disabled={submitted} onClick={() => onPick(i)} role="radio" aria-checked={picked}
            data-testid={`quiz-q${idx + 1}-option-${i}`}
            className={`flex w-full items-center gap-3 rounded-2xl border bg-white px-4 py-3.5 text-left transition-[border-color,background-color] ${cls}`}>
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${picked ? "border-bx" : "border-[#D6CBC1]"}`}>
              {picked && <span className="h-2.5 w-2.5 rounded-full bg-bx" />}
            </span>
            <span className="flex-1">{opt}</span>
            {submitted && correct && <Check className="h-5 w-5 text-[#3F7A5A]" />}
            {submitted && picked && !correct && <X className="h-5 w-5 text-[#B5485A]" />}
          </button>
        );
      })}
      {submitted && (
        <p className="rounded-2xl bg-ivory px-4 py-3 text-sm leading-relaxed text-ink/85" data-testid={`quiz-q${idx + 1}-explanation`}>
          <span className="font-semibold">{answer === q.correct ? "Bonne réponse. " : "Pas tout à fait. "}</span>{q.explanation}
        </p>
      )}
    </fieldset>
  );
}

export default function Mission1() {
  const { state, update, setCompleted } = useProgress();
  const { answers, submitted } = state.m1;
  const score = quizScore(answers);
  const allAnswered = QUIZ.every((q) => answers[q.id] !== undefined);

  const submit = () => {
    const s = quizScore(answers);
    update("m1", { submitted: true });
    setCompleted(1, s >= QUIZ_PASS);
    s >= QUIZ_PASS ? toast.success(`Quiz réussi : ${s}/3. Mission 01 validée !`) : toast(`${s}/3 — lis les corrections et retente ta chance.`);
  };
  const retry = () => {
    update("m1", { answers: {}, submitted: false });
    setCompleted(1, false);
  };

  return (
    <MissionShell id={1} done={isDone(state, 1)} validate={null}>
      <Card>
        <p className="text-soft">Trois questions pour vérifier l'essentiel. Il faut au moins 2 bonnes réponses sur 3 pour valider la mission.</p>
        <div className="mt-6 space-y-8">
          {QUIZ.map((q, i) => (
            <Question key={q.id} q={q} idx={i} answer={answers[q.id]} submitted={submitted} onPick={(v) => update("m1", { answers: { ...answers, [q.id]: v } })} />
          ))}
        </div>
        <div className="mt-8 flex flex-col gap-4 border-t border-line pt-6 sm:flex-row sm:items-center sm:justify-between">
          {submitted ? (
            <>
              <p className="font-display text-xl text-bx" data-testid="quiz-score">Ton score : {score}/3 {score >= QUIZ_PASS ? "— bravo !" : ""}</p>
              <Btn variant="outline" onClick={retry} data-testid="quiz-retry-button"><RotateCcw className="h-4 w-4" />Recommencer le quiz</Btn>
            </>
          ) : (
            <>
              <p className="text-sm text-soft">{allAnswered ? "Toutes les questions ont une réponse." : "Réponds aux trois questions pour voir les corrections."}</p>
              <Btn onClick={submit} disabled={!allAnswered} data-testid="quiz-submit-button"><Check className="h-4 w-4" />Voir les corrections</Btn>
            </>
          )}
        </div>
      </Card>
    </MissionShell>
  );
}
